import { LEVELS_CONFIG, LevelPieceType, MAX_TABLE_LAYERS, maxTableGridCols } from "../../../level_config.js";
import type { LevelConfig, LevelLayer, LevelPiece } from "../../../level_config.js";
import { LevelPackService } from "../../../level_pack/level_pack_service.js";
import { ConveyorLevelsService } from "../../../conveyor_config/conveyor_levels_service.js";
import { ConveyorConfigService } from "../../../conveyor_config/conveyor_config_service.js";
import type { ConveyorConfig } from "../../../conveyor_config/conveyor_config_service.js";
import { towerTopHeight } from "./field_layout.js";
import { BlockUnlocks } from "../../../level_pack/block_unlocks.js";
import { StatisticsStorage } from "../../../storage/statistics_storage.js";

export interface EscalatorStructure
{
  layers: LevelLayer[];
  cols: number;
  depth: number;
  rows: number;
  heavy: boolean;
}

const FALLBACK_LAYERS: LevelLayer[] = [ {
  grid: [ [ 1 ] ],
  pieces: [ { id: 1, type: LevelPieceType.Cube, x: 0, y: 0, width: 1, height: 1 } ],
} ];

export class EscalatorStructures
{
  private static readonly COLUMN_MAX_BLOCKS = 3;
  private static readonly LONG_COLUMN_MAX_BLOCKS = 5;
  private static readonly COLUMN_TYPES = [
    LevelPieceType.Jar,
    LevelPieceType.Metal,
    LevelPieceType.RoundMetal,
    LevelPieceType.IronCan,
    LevelPieceType.Ice,
  ];
  private static readonly LONG_COLUMN_TYPES = [
    LevelPieceType.Tree,
    LevelPieceType.Plastic,
  ];

  private readonly settings: ConveyorConfig = ConveyorConfigService.get();
  private readonly unlocks = new BlockUnlocks( StatisticsStorage.load().level );
  private readonly pool: EscalatorStructure[];
  private readonly light_indices: number[];
  private readonly heavy_indices: number[];
  private last_index = -1;

  constructor( levels?: number[] )
  {
    const pool = EscalatorStructures.sources( levels ).flatMap( config => this.fromConfig( config ) );

    this.pool = pool.length > 0 ? pool : [ this.measure( FALLBACK_LAYERS ) ];
    this.light_indices = this.pool.flatMap( ( structure, index ) => structure.heavy ? [] : [ index ] );
    this.heavy_indices = this.pool.flatMap( ( structure, index ) => structure.heavy ? [ index ] : [] );
  }

  public maxCols(): number
  {
    return Math.max( ...this.pool.map( s => s.cols ) );
  }

  public maxDepth(): number
  {
    return Math.max( ...this.pool.map( s => s.depth ) );
  }

  public maxRows(): number
  {
    return Math.max( ...this.pool.map( s => s.rows ) );
  }

  public next(): EscalatorStructure
  {
    const candidates = this.candidates();
    const fresh = candidates.filter( index => index !== this.last_index );
    const choices = fresh.length > 0 ? fresh : candidates;
    const index = choices[Math.floor( Math.random() * choices.length )];

    this.last_index = index;

    const structure = this.pool[index];
    return { ...structure, layers: this.withAvailableBlocks( structure.layers ) };
  }

  private candidates(): number[]
  {
    const want_heavy = Math.random() < this.heavyChance();
    const preferred = want_heavy ? this.heavy_indices : this.light_indices;

    return preferred.length > 0 ? preferred : this.pool.map( ( _, index ) => index );
  }

  private heavyChance(): number
  {
    if ( this.last_index < 0 )
      return this.settings.heavy_first_chance;

    return this.pool[this.last_index].heavy
      ? this.settings.heavy_after_heavy_chance
      : this.settings.heavy_after_light_chance;
  }

  private static sources( levels?: number[] ): LevelConfig[]
  {
    const by_level = new Map<number, LevelConfig>();
    const served = ConveyorLevelsService.getConfigs();
    const fallback = served.length > 0 ? [] : [ ...LEVELS_CONFIG, ...LevelPackService.getCachedConfigs() ];

    for ( const config of [ ...fallback, ...served ] )
      by_level.set( config.level, config );

    if ( !levels?.length )
      return [ ...by_level.values() ];

    return levels.flatMap( level => by_level.get( level ) ?? [] );
  }

  private fromConfig( config: LevelConfig ): EscalatorStructure[]
  {
    const stacks = config.tables?.length
      ? config.tables.map( table => table.layers )
      : [ config.layers ?? [] ];

    return stacks
      .map( layers => EscalatorStructures.trim( layers.slice( 0, MAX_TABLE_LAYERS ) ) )
      .filter( layers => layers.length > 0 )
      .filter( layers => EscalatorStructures.widest( layers ) <= this.settings.structure_max_cols )
      .filter( layers => this.canPlace( layers ) )
      .map( layers => this.measure( layers ) )
      .filter( structure => structure.depth <= this.settings.structure_max_depth
        && structure.rows <= this.settings.structure_max_rows );
  }

  private static widest( layers: LevelLayer[] ): number
  {
    return Math.max( ...layers.map( layer => layer.grid[0]?.length ?? 0 ) );
  }

  private static trim( layers: LevelLayer[] ): LevelLayer[]
  {
    const filled = layers.map( layer => layer.pieces.length > 0 );
    const first = filled.indexOf( true );
    const last = filled.lastIndexOf( true );

    return first < 0 ? [] : layers.slice( first, last + 1 );
  }

  private needsReplacement( piece: LevelPiece ): boolean
  {
    return piece.type === LevelPieceType.Stone || !this.unlocks.isUnlocked( piece.type, piece.variant, EscalatorStructures.tier( piece ) );
  }

  private static tier( piece: LevelPiece ): number
  {
    const long_side = Math.max( piece.width, piece.height );

    return Math.min( piece.width, piece.height ) === 1 && Number.isInteger( long_side ) ? long_side : 1;
  }

  private canPlace( layers: LevelLayer[] ): boolean
  {
    return layers.every( layer => layer.pieces.every( piece => !this.needsReplacement( piece ) || this.replacements( piece ).length > 0 ) );
  }

  private withAvailableBlocks( layers: LevelLayer[] ): LevelLayer[]
  {
    return layers.map( layer => ( { ...layer, pieces: layer.pieces.map( piece => this.availablePiece( piece ) ) } ) );
  }

  private availablePiece( piece: LevelPiece ): LevelPiece
  {
    const types = this.needsReplacement( piece ) ? this.replacements( piece ) : [];
    const swapped = types.length > 0
      ? { ...piece, type: EscalatorStructures.pick( types ), variant: undefined }
      : piece;
    const cube_variants = this.unlocks.cubeVariants( EscalatorStructures.tier( swapped ) );

    return swapped.type === LevelPieceType.Cube && swapped.variant === undefined && cube_variants.length > 0
      ? { ...swapped, variant: EscalatorStructures.pick( cube_variants ) }
      : swapped;
  }

  private replacements( piece: LevelPiece ): LevelPieceType[]
  {
    const tier = EscalatorStructures.tier( piece );

    return EscalatorStructures.sameShapeTypes( piece ).filter( type => this.unlocks.isUnlocked( type, undefined, tier ) );
  }

  private static pick<T>( items: T[] ): T
  {
    return items[Math.floor( Math.random() * items.length )];
  }

  private static sameShapeTypes( piece: LevelPiece ): LevelPieceType[]
  {
    const types = [ LevelPieceType.Cube ];
    const rotated = !!( piece.rotationDegX || piece.rotationDegY || piece.rotationDegZ );

    if ( rotated )
      return types;

    const long_side = Math.max( piece.width, piece.height );
    const is_column = Math.min( piece.width, piece.height ) === 1 && Number.isInteger( long_side );

    if ( piece.width === 1 && piece.height === 1 )
      types.push( LevelPieceType.Watermelon );

    if ( is_column && long_side <= EscalatorStructures.COLUMN_MAX_BLOCKS )
      types.push( ...EscalatorStructures.COLUMN_TYPES );

    if ( is_column && long_side <= EscalatorStructures.LONG_COLUMN_MAX_BLOCKS )
      types.push( ...EscalatorStructures.LONG_COLUMN_TYPES );
    else if ( piece.width === 2 && piece.height === 2 )
      types.push( LevelPieceType.Plastic );

    return types;
  }

  private measure( layers: LevelLayer[] ): EscalatorStructure
  {
    const offsets = layers.flatMap( layer => layer.pieces.map( piece => piece.offsetZ ?? 0 ) );
    const spread = Math.max( ...offsets ) - Math.min( ...offsets );
    const widest = EscalatorStructures.widest( layers );
    const depth = Math.ceil( layers.length + spread );
    const rows = towerTopHeight( layers, maxTableGridCols(), MAX_TABLE_LAYERS );

    return {
      layers,
      cols: Math.min( widest, maxTableGridCols() ),
      depth,
      rows,
      heavy: depth > this.settings.light_max_depth
        && widest > this.settings.light_max_cols
        && rows > this.settings.light_max_rows,
    };
  }
}
